"use client";

import { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import {
  FILTER_CATEGORY_KEYS,
  FACET_TOOLTIP_KEYS,
  LEARNING_TYPE_SUBTITLE_KEYS,
  PRODUCT_TYPE_KEYS,
} from "@/components/Search/constants";

const toMessageKey = (key: string) => key.replace(/\./g, "_");

export function useSearchMessages() {
  const t = useTranslations("Search");

  const getFilterCategoryName = useCallback(
    (filterCategory: string): string => {
      if (
        FILTER_CATEGORY_KEYS.includes(
          filterCategory as (typeof FILTER_CATEGORY_KEYS)[number],
        )
      ) {
        return t(
          `filterCategories.${toMessageKey(filterCategory)}` as Parameters<
            typeof t
          >[0],
        );
      }
      return filterCategory;
    },
    [t],
  );

  const getProductTypeTitle = useCallback(
    (productType: string): string => {
      if (
        PRODUCT_TYPE_KEYS.includes(
          productType as (typeof PRODUCT_TYPE_KEYS)[number],
        )
      ) {
        return t(`productTypeTitles.${productType}` as Parameters<typeof t>[0]);
      }
      return productType;
    },
    [t],
  );

  const getFacetTooltip = useCallback(
    (facet: string): string | undefined => {
      if (
        FACET_TOOLTIP_KEYS.includes(
          facet as (typeof FACET_TOOLTIP_KEYS)[number],
        )
      ) {
        return t(
          `facetsWithTooltip.${toMessageKey(facet)}` as Parameters<typeof t>[0],
        );
      }
      return undefined;
    },
    [t],
  );

  const getLearningTypeSubtitle = useCallback(
    (itemValue: string): string | undefined => {
      if (
        LEARNING_TYPE_SUBTITLE_KEYS.includes(
          itemValue as (typeof LEARNING_TYPE_SUBTITLE_KEYS)[number],
        )
      ) {
        return t(
          `learningTypeSubtitles.${itemValue}` as Parameters<typeof t>[0],
        );
      }
      return undefined;
    },
    [t],
  );

  const getDisplayLabel = useCallback(
    (label: string, filterCategory: string): string => {
      if (filterCategory === "availability" && label === "Current") {
        return t("availabilityLabels.Current");
      }
      return label;
    },
    [t],
  );

  const getSearchFilterPlaceholder = useCallback(
    (filterCategory: string, count: number): string => {
      return t("searchFilterPlaceholder", {
        category: getFilterCategoryName(filterCategory).toLowerCase(),
        count,
      });
    },
    [getFilterCategoryName, t],
  );

  const getSearchFilterAriaLabel = useCallback(
    (filterCategory: string): string => {
      return t("searchFilterAriaLabel", {
        category: getFilterCategoryName(filterCategory).toLowerCase(),
      });
    },
    [getFilterCategoryName, t],
  );

  return useMemo(
    () => ({
      getFilterCategoryName,
      getProductTypeTitle,
      getFacetTooltip,
      getLearningTypeSubtitle,
      getDisplayLabel,
      getSearchFilterPlaceholder,
      getSearchFilterAriaLabel,
    }),
    [
      getDisplayLabel,
      getFacetTooltip,
      getFilterCategoryName,
      getLearningTypeSubtitle,
      getProductTypeTitle,
      getSearchFilterAriaLabel,
      getSearchFilterPlaceholder,
    ],
  );
}
